'use client'
import {
  useCartMiniQuery,
  useCountCartItemQuery,
} from '@/hooks/react-query/cart/use-cart'
import { formatPrice } from '@/lib/utils'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { CartMiniItem, CartMiniItemSkeleton } from './cart-mini-item'

// Helper function to get random empty cart message
const getRandomEmptyCartMessage = (): string => {
  const texts = [
    'Đẹp có sẵn rồi, giờ chỉ thiếu đồ Coolmate để hoàn hảo hơn.',
    'Gương nói bạn đẹp, nhưng Coolmate nói bạn còn có thể đẹp hơn nữa.',
    'Người ta có đôi có cặp, còn giỏ hàng của bạn thì… trống trơn',
  ]

  const randomIndex = Math.floor(Math.random() * texts.length)
  return texts[randomIndex]
}

export function CartMini() {
  const { data: countCartItem, isLoading } = useCountCartItemQuery()
  const [hasHovered, setHasHovered] = useState(false)

  const handleMouseEnter = () => {
    if (!hasHovered) {
      setHasHovered(true)
    }
  }

  return (
    <div
      className='group relative cursor-pointer'
      onMouseEnter={handleMouseEnter}
    >
      <Link
        href={'/cart'}
        aria-label='Giỏ hàng'
        data-tracking-level-3={'CartMini'}
      >
        <div className='relative flex h-13 w-13 cursor-pointer items-center justify-center'>
          <Image
            src={'/icons/icon-cart.svg'}
            alt='cart'
            width={24}
            height={24}
            className='h-6 w-6'
          />
          {/* Fake để nhận biết version mới */}
          <span className='absolute bottom-1/2 right-1/2 flex h-[3px] w-[3px] translate-x-1/2 translate-y-1/3 items-center justify-center rounded-full bg-white'></span>
          {countCartItem && (
            <span className='absolute bottom-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-error-500 text-xxs text-white'>
              {isLoading ? '' : countCartItem?.count}
            </span>
          )}
        </div>
      </Link>

      <div className="invisible absolute right-0 top-14 z-[999] w-[400px] translate-y-2 rounded-md border border-neutral-100 bg-light p-0 opacity-0 shadow transition-all duration-300 ease-out before:pointer-events-none before:absolute before:-top-8 before:right-3 before:h-0 before:w-0 before:border-[16px] before:border-solid before:border-transparent before:border-b-light before:drop-shadow-[0_-1px_1px_rgba(0,0,0,0.1)] before:filter before:content-[''] lg:group-hover:visible lg:group-hover:translate-y-0 lg:group-hover:opacity-100">
        <div className='py-4'>
          <CartMiniContent shouldLoad={hasHovered} />
        </div>
      </div>
    </div>
  )
}

const CartMiniContent = ({ shouldLoad }: { shouldLoad: boolean }) => {
  const { data: cartMini, isLoading } = useCartMiniQuery({
    enabled: shouldLoad, // Chỉ load khi đã hover
  })

  const isCartEmpty = !cartMini?.items || cartMini.items.length === 0

  // Show skeleton while loading or haven't loaded yet
  if (!shouldLoad || isLoading) {
    return (
      <>
        <div className='mb-6 flex items-center justify-between px-4'>
          <div className='flex items-center gap-2'>
            <div className='h-4 w-16 animate-pulse rounded bg-neutral-200' />
            <div className='h-5 w-20 animate-pulse rounded bg-neutral-200' />
            <div className='h-4 w-24 animate-pulse rounded bg-neutral-200' />
          </div>
          <div className='h-4 w-16 animate-pulse rounded bg-neutral-200' />
        </div>

        <div className='space-y-4 px-4 pr-2'>
          {Array.from({ length: 3 }).map((_, index) => (
            <CartMiniItemSkeleton key={index} />
          ))}
        </div>
      </>
    )
  }

  if (isCartEmpty) {
    return (
      <div className='text-center'>
        <div className='mb-4'>
          <Image
            src={'/icons/icon-cart.svg'}
            alt='empty cart'
            width={40}
            height={40}
            className='mx-auto opacity-40'
          />
        </div>
        <p className='text-sm leading-5 text-neutral-600'>
          {getRandomEmptyCartMessage()}
        </p>
      </div>
    )
  }

  return (
    <>
      <div className='mb-6 flex items-center justify-between px-4'>
        <div className='flex items-center gap-2 text-sm leading-4.5 text-neutral-400'>
          <span>Tạm tính:</span>
          <span className='text-base font-bold text-neutral-900'>
            {formatPrice(cartMini?.price ?? 0)}
          </span>
          <span>({cartMini?.number_item} sản phẩm)</span>
        </div>

        <Link
          href='/cart'
          className='text-sm leading-4.5 text-primary hover:text-primary-400 hover:underline'
        >
          Xem tất cả
        </Link>
      </div>

      <div className='scrollbar max-h-[380px] space-y-4 overflow-y-auto px-4 pr-2'>
        {cartMini?.items.map((item, index) => (
          <CartMiniItem
            id={item.id}
            name={item.title}
            image={item.images.src}
            regularPrice={item.variants.regular_price}
            originalPrice={item.variants.compare_price}
            variantTitle={item.variants.title}
            quantity={item.quantity}
            link={'/cart'}
            key={`${item.id}-${index}`}
            variantId={item.variants.id}
            indexId={item.index_id}
            offPercent={item.off_percent}
          />
        ))}
      </div>
    </>
  )
}
